import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { 
  Box,
  Button,
  CircularProgress,
  Container, 
  Typography 
} from '@mui/material';

import PublicRoutes from './publicRoutes';
import ProtectedRoutes from './protectedRoutes';
import packageJson from "../../package.json"; 

// Routes — eager loaded (small bundles)
import AuthPage from '../views/authPage/AuthPage';
import HomePage from '../views/homePage/HomePage';
import PricingPage from '../views/pricingPage/PricingPage';
import ProfilePage from '../views/profilePage/ProfilePage';
import BillingSuccessPage from '../views/billingSuccessPage/BillingSuccessPage';
import LandingPage from '../views/landingPage/LandingPage';
import TermsOfServicePage from '../views/termsOfServicePage/TermsOfServicePage';
import PrivacyPolicyPage from '../views/privacyPolicyPage/PrivacyPolicyPage';
import TopBar from '../components/topBar/TopBar';

import { trackPageView } from '../utils/analytics';

// Lazy-loaded — Editor.js + all its plugins only download when a note is opened
const NotesPage = lazy(() => import('../views/notesPage/NotesPage'));

// Minimal full-screen loader shown while NotesPage bundle downloads
const PageLoader = () => (
  <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <CircularProgress size={32} sx={{ color: '#8b5cf6' }} />
  </Box>
);

// Tracks page navigation — only fires when GA4 is initialized (after consent)
const PageTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
  return null;
};

// Layout for all protected pages: fixed topbar + content offset
const AppLayout = () => (
  <>
    <TopBar />
    <div style={{ paddingTop: "56px" }}>
      <Outlet />
    </div>
  </>
);

const MainRouter = () => {
  
  return (
    <Router>
      <PageTracker />
      <Routes>

        {/* ── Public routes (always accessible) ── */}
        <Route path='/' element={<LandingPage />} />
        <Route path='/terms' element={<TermsOfServicePage />} />
        <Route path='/privacy' element={<PrivacyPolicyPage />} />

        {/* Public — only accessible when logged OUT */}
        <Route element={<PublicRoutes />}>
          <Route path='/auth' element={<AuthPage />} />
        </Route>

        {/* ── Protected — only accessible when logged IN ── */}
        <Route element={<ProtectedRoutes />}>
          <Route element={<AppLayout />}>
            <Route path='/app'              element={<HomePage />} />
            <Route path='/pricing'          element={<PricingPage />} />
            <Route path='/profile'          element={<ProfilePage />} />
            <Route path='/billing/success'  element={<BillingSuccessPage />} />
            <Route
              path='/app/:id'
              element={
                <Suspense fallback={<PageLoader />}>
                  <NotesPage />
                </Suspense>
              }
            />
          </Route>
        </Route>

        <Route path="/version" element={<AppVersion />} />
        <Route path='*' element={<NotFoundPage />} />
      </Routes>
    </Router>
  )
}

export default MainRouter;

// 404 page
const NotFoundPage = () => {
  const navigate = useNavigate();
  return(
    <>
      <Helmet>
        <title>Page Not Found — NoteCORE</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Container maxWidth="lg">
        <Box sx={{ height: "100vh" }} 
          gap={"40px"}
          className="flexCenterCenterColumn">
          <Typography variant='h4'>404 - Page not found</Typography>
          <Button 
            variant='contained'
            onClick={() => navigate("/")}>
            Go Home
          </Button>
        </Box>
      </Container>
    </>
  )
};

// Version page
const AppVersion = () => (
  <Container maxWidth="lg">
    <Box sx={{ height: "100vh" }} className="flexCenterCenterColumn">
      <Typography variant="body1">v{packageJson.version}</Typography>
    </Box>
  </Container>
);